// The confirmation in front of the backoffice's one write on placements: Undo
// (`architecture/0027`). The Categories tab's change log and the document viewer
// both open it, so the words and the refusal handling are the same in both.
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUndoChange } from "./projects-api";

/** The change being asked about, and a line that says which one it is. */
export type UndoTarget = { changeId: string; summary: string };

/**
 * Open while `target` is set. An undo changes what the tenant's own users see,
 * so it is never one click. A refusal stays inside the dialog: a 409 is an
 * answer to this question, and the admin should read it before moving on.
 */
export function UndoChangeDialog({
  tenantId,
  projectId,
  target,
  onClose,
}: {
  tenantId: string;
  projectId: string;
  target: UndoTarget | null;
  onClose: () => void;
}) {
  const undo = useUndoChange(tenantId, projectId);

  // Every way out goes through here, so the next change opens with no
  // leftover refusal from this one.
  const close = (): void => {
    undo.reset();
    onClose();
  };

  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Undo this placement?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-[0.85rem] leading-[1.5] text-muted">
            Every file this change touched goes back to how it was before, and the tenant sees that
            at once. The undo is a new entry in the change log, with no user. The files then count
            as placed by a person, so a later import will not move them.
          </p>
          <p className="truncate text-[0.82rem] font-semibold text-ink">{target?.summary}</p>
          {/* The server's words, as they are: each refusal is a full sentence
              that says why ("These files have changed since, so this can’t
              be undone."). */}
          {undo.isError && (
            <p role="alert" className="text-[0.82rem] text-danger">
              {undo.error.message}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={close}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={undo.isPending}
            onClick={() => {
              if (!target) return;
              undo.mutate(target.changeId, { onSuccess: close });
            }}
          >
            {undo.isPending ? "Undoing…" : "Undo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
